import { Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { assetURL } from '@/lib/api';
import { getCachedModelURL } from '@/lib/modelCache';
import { prettyModelName } from '@/lib/utils';

interface Props {
  modelPath: string;
  externalURL?: string;
  /**
   * A-Frame world-scale applied to the model on the AR marker (the
   * `scaling` value from the sequence JSON, parsed to a scalar).
   * Displayed as a chip so operators can see how large the model will
   * appear on the physical marker without having to open the file.
   */
  scale?: number;
  /** Reports the GLB's embedded clip names once loaded ([] if none). */
  onClips?: (names: string[]) => void;
}

/**
 * Live 3D preview of the currently-broadcast model using Google's
 * <model-viewer> web component. Loaded via CDN in index.html.
 *
 * `src` is a cached blob URL so revisits are instant and multiple
 * <model-viewer> mounts of the same GLB share a single download.
 *
 * If the GLB contains embedded animations we surface a Play/Pause
 * button (top-right of the preview) — GLB animation autoplays to
 * match the AR client but the operator can pause for a still shot.
 */
export function ModelPreview({ modelPath, scale, onClips }: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [envURL, setEnvURL] = useState<string | null>(null);
  const [hasAnimation, setHasAnimation] = useState(false);
  const [playing, setPlaying] = useState(true);
  const viewerRef = useRef<HTMLElement | null>(null);

  // Resolve the shared studio HDR URL once. Points at the same file the
  // AR client's hdr-environment component uses, so the preview lighting
  // matches what you'll see through the camera.
  useEffect(() => {
    let cancelled = false;
    assetURL('/assets/env/studio_small_09_1k.hdr')
      .then((u) => {
        if (!cancelled) setEnvURL(u);
      })
      .catch(() => {
        if (!cancelled) setEnvURL(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setHasAnimation(false);
    setPlaying(true);
    onClips?.([]);
    getCachedModelURL(modelPath)
      .then((u) => {
        if (!cancelled) setSrc(u);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [modelPath]);

  // Detect embedded animations once the model has loaded.
  useEffect(() => {
    const el = viewerRef.current;
    if (!el || !src) return;
    const onLoad = () => {
      const anims = (el as unknown as { availableAnimations?: string[] })
        .availableAnimations;
      const has = Array.isArray(anims) && anims.length > 0;
      setHasAnimation(has);
      onClips?.(has ? anims! : []);
      if (has) {
        // Default to playing to match the AR client.
        (el as unknown as { play?: () => void }).play?.();
        setPlaying(true);
      }
    };
    el.addEventListener('load', onLoad);
    return () => el.removeEventListener('load', onLoad);
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAnimation = () => {
    const el = viewerRef.current as unknown as {
      play?: () => void;
      pause?: () => void;
    } | null;
    if (!el) return;
    if (playing) {
      el.pause?.();
      setPlaying(false);
    } else {
      el.play?.();
      setPlaying(true);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-md border bg-gradient-to-br from-cisco-navy/5 via-transparent to-primary/5">
      <div className="absolute left-3 top-3 z-10 flex items-center gap-2">
        <span className="rounded-md bg-background/80 px-2 py-1 text-xs font-medium shadow-sm backdrop-blur">
          {prettyModelName(modelPath)}
        </span>
        {typeof scale === 'number' && (
          <span
            className="rounded-md bg-background/80 px-2 py-1 text-[11px] font-medium tabular-nums text-muted-foreground shadow-sm backdrop-blur"
            title="A-Frame world scale applied to the model on the AR marker."
          >
            scale {scale.toFixed(2)}
          </span>
        )}
      </div>
      {hasAnimation && (
        <button
          type="button"
          onClick={toggleAnimation}
          aria-label={playing ? 'Pause animation' : 'Play animation'}
          title={playing ? 'Pause animation' : 'Play animation'}
          className="absolute right-3 top-3 z-10 inline-flex h-8 items-center gap-1.5 rounded-md bg-background/80 px-2 text-xs font-medium shadow-sm backdrop-blur transition-colors hover:bg-background"
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          <span>{playing ? 'Pause' : 'Play'}</span>
        </button>
      )}
      {src ? (
        <model-viewer
          key={src}
          ref={(el: HTMLElement | null) => {
            viewerRef.current = el;
          }}
          src={src}
          alt={prettyModelName(modelPath)}
          camera-controls
          auto-rotate
          autoplay
          shadow-intensity="1"
          exposure="1.0"
          tone-mapping="aces"
          environment-image={envURL ?? 'neutral'}
          loading="eager"
          style={{ width: '100%', height: '360px', background: 'transparent' }}
        />
      ) : (        <div className="grid h-[360px] place-items-center text-sm text-muted-foreground">
          Loading preview…
        </div>
      )}
    </div>
  );
}
